import React, { useState, useEffect, useMemo } from 'react';
import { getTreeUsers, createTreeUser, updateTreeUser, deleteTreeUser, getRoles } from '../../lib/treeApi';
import { useTreeAuth } from '../../lib/treeAuth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, Pencil, Shield, Truck, TreePine, Users as UsersIcon, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BUILTIN_META = {
  admin: { icon: Shield, color: '#1A4331', desc: 'Full access to everything' },
  manager: { icon: UsersIcon, color: '#0369a1', desc: 'Full access, except Users and Settings' },
  field_status: { icon: Truck, color: '#E27B58', desc: 'Only marks pallets ready in the field' },
  field_collector: { icon: TreePine, color: '#16a34a', desc: 'Only confirms pallets returned to the yard' },
};

export default function TreeUsersPage() {
  const { user: me } = useTreeAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', username: '', password: '', role: 'field_collector' });

  const ROLE_META = useMemo(() => ({
    admin: { label: 'Admin', ...BUILTIN_META.admin },
    ...Object.fromEntries(roles.map((r) => [r.key, {
      label: r.name,
      icon: BUILTIN_META[r.key]?.icon || ShieldCheck,
      color: BUILTIN_META[r.key]?.color || '#64748b',
      desc: BUILTIN_META[r.key]?.desc || 'Custom role — see Roles page for its permissions',
    }])),
  }), [roles]);

  const fetchUsers = async () => {
    setLoading(true);
    try { const { data } = await getTreeUsers(); setUsers(data); } catch { toast.error('Failed to load users'); }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); getRoles().then(r => setRoles(r.data)).catch(() => {}).finally(() => setRolesLoaded(true)); }, []);

  const resetForm = () => setForm({ name: '', username: '', password: '', role: 'field_collector' });

  const handleCreate = async () => {
    if (!form.name.trim() || !form.username.trim() || !form.password) return toast.error('Fill in all fields');
    try {
      await createTreeUser(form);
      toast.success('User created');
      setShowCreate(false);
      resetForm();
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
  };

  const openEdit = (u) => { setEditingUser(u); setForm({ name: u.name, username: u.email, password: '', role: u.role }); };

  const handleSaveEdit = async () => {
    try {
      await updateTreeUser(editingUser.id, { name: form.name, role: form.role, password: form.password || undefined });
      toast.success('User updated');
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (u) => {
    try { await deleteTreeUser(u.id); toast.success('User removed'); fetchUsers(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="tree-users-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1711] font-['Outfit'] tracking-tight">Users</h1>
          <p className="text-sm text-[#4A5D53] mt-1">Manage who can access the Tree Management System</p>
        </div>
        <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A4331] text-white hover:bg-[#1A4331]/90 rounded-lg" data-testid="tree-create-user-button">
              <Plus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tree-create-user-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit']">Add User</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Full Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. John Smith" data-testid="tree-user-name-input" />
              </div>
              <div>
                <Label>Username</Label>
                <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="Used to log in" data-testid="tree-user-username-input" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="At least 4 characters" data-testid="tree-user-password-input" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="tree-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_META).map(([key, meta]) => (
                      <SelectItem key={key} value={key}>{meta.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#4A5D53] mt-1">{ROLE_META[form.role]?.desc}</p>
              </div>
              <Button onClick={handleCreate} className="w-full bg-[#1A4331] text-white" data-testid="tree-submit-user-button">Create User</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#1A4331]/15 rounded-xl overflow-hidden" data-testid="tree-users-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F1ED]/50">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Username</TableHead>
              <TableHead className="font-semibold">Role</TableHead>
              <TableHead className="font-semibold w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && !loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-[#4A5D53]"><UsersIcon className="w-6 h-6 mx-auto mb-2 opacity-40" />No users yet</TableCell></TableRow>
            ) : !rolesLoaded ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10 text-[#4A5D53]">Loading…</TableCell></TableRow>
            ) : (
              users.map((u) => {
                const meta = ROLE_META[u.role] || { label: u.role, icon: ShieldCheck, color: '#64748b', desc: '' };
                const Icon = meta.icon;
                return (
                  <TableRow key={u.id} className="hover:bg-[#F4F1ED]/30" data-testid={`tree-user-row-${u.id}`}>
                    <TableCell className="font-medium text-[#0A1711]">{u.name} {u.id === me?.id && <span className="text-[10px] text-[#4A5D53]">(you)</span>}</TableCell>
                    <TableCell className="text-sm text-[#4A5D53]">{u.email}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-[#4A5D53] hover:text-[#1A4331]" data-testid={`tree-edit-user-${u.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u)} className="text-red-400 hover:text-red-600" data-testid={`tree-delete-user-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editingUser} onOpenChange={(open) => { if (!open) { setEditingUser(null); resetForm(); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tree-edit-user-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit User</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Full Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="tree-edit-user-name-input" />
            </div>
            <div>
              <Label>Username</Label>
              <Input value={form.username} disabled className="bg-gray-50 text-[#4A5D53]" />
            </div>
            <div>
              <Label>New Password (optional)</Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to keep current password" data-testid="tree-edit-user-password-input" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="tree-edit-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_META).map(([key, meta]) => (
                    <SelectItem key={key} value={key}>{meta.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#4A5D53] mt-1">{ROLE_META[form.role]?.desc}</p>
            </div>
            <Button onClick={handleSaveEdit} className="w-full bg-[#1A4331] text-white" data-testid="tree-save-edit-user-button">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
